import React from "react";
import { IconBuildingBank, IconCreditCard, IconExternalLink } from "@tabler/icons-react";

import "./styles.scss";

import { Button, Card, StatusChip, Text } from "@fragments";
import banks from "@helpers/banks";
import Parser from "@helpers/parser.js";
import { WALLET_PROVIDERS, walletIsConnected } from "@helpers/wallets";

export default function ConnectedAccount({
    provider,
    wallet,
    loading,
    onRegisterAccount,
}) {
    const activeProvider = provider || wallet?.provider || WALLET_PROVIDERS.ASAAS;

    return (
        <Card
            flat
            className="withdrawals-connected-account"
            title="Conta conectada para saques"
            subtitle={activeProvider === WALLET_PROVIDERS.STRIPE
                ? "Conta internacional usada para payouts em USD."
                : "Conta bancária usada para transferências da carteira nacional."}
            icon={activeProvider === WALLET_PROVIDERS.STRIPE ? <IconCreditCard /> : <IconBuildingBank />}
        >
            {loading ? (
                <Loading />
            ) : activeProvider === WALLET_PROVIDERS.STRIPE ? (
                <StripeAccount wallet={wallet} onRegisterAccount={onRegisterAccount} />
            ) : (
                <AsaasAccount wallet={wallet} onRegisterAccount={onRegisterAccount} />
            )}
        </Card>
    );
}

function StripeAccount({ wallet, onRegisterAccount }) {
    const connected = walletIsConnected(wallet);
    const onboarding = wallet?.onboarding || {};

    return (
        <div className="connected-account-card connected-account-card--stripe">
            <div className="left-section">
                <IconCreditCard className="bank-fallback-icon" />
            </div>

            <div className="right-section">
                <div className="info-row">
                    <Text.label contrast={9}>
                        Stripe Express
                    </Text.label>
                    {connected ? (
                        <StatusChip.Success text="Ativa" />
                    ) : onboarding.detailsSubmitted ? (
                        <StatusChip.Alert text="Pendente" />
                    ) : (
                        <StatusChip.Default text="Não conectada" />
                    )}
                </div>

                <Text.span className="info-row" contrast={6}>
                    {connected
                        ? "Payouts em USD habilitados"
                        : "Conclua o onboarding da conta internacional"}
                </Text.span>
            </div>

            <Button
                color="overlay"
                size="small"
                startIcon={<IconExternalLink />}
                onClick={onRegisterAccount}
            >
                {connected ? "Gerenciar" : "Conectar"}
            </Button>
        </div>
    );
}

function AsaasAccount({ wallet, onRegisterAccount }) {
    if (!wallet?.withdrawal) {
        return (
            <div className="connected-account-card connected-account-card--empty">
                <div className="right-section">
                    <Text.label contrast={8}>Conta de recebíveis não cadastrada</Text.label>
                    <Text.span contrast={6}>Cadastre uma chave Pix para liberar saques em BRL.</Text.span>
                </div>
                <Button text="Cadastrar conta" onClick={onRegisterAccount} size="small" />
            </div>
        );
    }

    const { withdrawal, statuses } = wallet;
    const { receiver } = withdrawal;

    const bankConfig = banks.find(b => parseInt(b.code) === parseInt(receiver?.bank?.code));
    const bankLogo = bankConfig?.logo || receiver?.bank?.logo;

    const statusConfig = {
        'approved': { text: 'Ativa', variant: 'success' },
        'active': { text: 'Ativa', variant: 'success' },
        'pending': { text: 'Pendente', variant: 'alert' },
        'rejected': { text: 'Reprovada', variant: 'error' },
        'processing': { text: 'Processando', variant: 'info' }
    }[statuses?.bank] || { text: statuses?.bank || 'Desconhecido', variant: 'default' };

    let keyFormatted = withdrawal.key;
    if (withdrawal.keyType === 'email') {
        keyFormatted = Parser.email(withdrawal.key);
    } else if (withdrawal.keyType === 'phone') {
        keyFormatted = Parser.phone('+55' + withdrawal.key);
    } else if (withdrawal.keyType === 'cpf') {
        keyFormatted = Parser.cpf(withdrawal.key);
    } else if (withdrawal.keyType === 'cnpj') {
        keyFormatted = Parser.cnpj(withdrawal.key);
    }

    return (
        <div className="connected-account-card">
            <div className="left-section">
                {bankLogo ? (
                    <img
                        src={bankLogo}
                        alt={receiver?.bank?.name || "Banco"}
                        className="bank-logo"
                    />
                ) : (
                    <IconBuildingBank className="bank-fallback-icon" />
                )}
            </div>

            <div className="right-section">
                <div className="info-row">
                    <Text.span contrast={7}>
                        <Text.span weight={500} contrast={7}>Chave Pix: </Text.span>
                        {keyFormatted}
                    </Text.span>

                    <StatusChip text={statusConfig.text} variant={statusConfig.variant} />
                </div>

                <Text.span className="info-row" contrast={7}>
                    {receiver?.owner?.name} {receiver?.owner?.document}
                </Text.span>
            </div>
        </div>
    );
}

function Loading() {
    return (
        <div className="connected-account-card connected-account-card--loading">
            <div className="left-section">
                <IconBuildingBank className="bank-fallback-icon" />
            </div>
            <div className="right-section">
                <Text.label loading contrast={8}>Conta conectada</Text.label>
                <Text.span loading contrast={6}>Dados da conta de saque</Text.span>
            </div>
        </div>
    );
}
